import { __assign } from '../../../../_virtual/_tslib.js';
import { sanitizeStructuredWiki, getWikiSanitizerWithOptions } from '@superbet-group/web.lib.vue-utilities';
import { isImageWithLink } from './htmlUtils.js';

/**
 * Performs HTML sanitization on the provided text and returns a VNodeData object.
 * @param text HTML string to be sanitized.
 * @param iframeHostnameWhitelist If provided, will permit rendering of iframes, but only from whitelisted hostnames.
 *                              If not provided, iframes will be removed from the HTML.
 *                              This is useful for rendering wiki content that may contain iframes.
 * @returns Sanitized VNodeData object with innerHTML property.
 */
function createInnerHtml(text, iframeHostnameWhitelist) {
    var sanitizeHtml = iframeHostnameWhitelist != null
        ? getWikiSanitizerWithOptions({
            iframeWhitelist: iframeHostnameWhitelist,
        })
        : sanitizeStructuredWiki;
    return { domProps: { innerHTML: sanitizeHtml(text) } };
}
function generateButton(createElement, href, text) {
    var isInternalLink = href.startsWith(window.location.origin);
    return createElement('div', { attrs: { class: 'wiki-button-wrapper' } }, [
        createElement('a', {
            attrs: {
                href: href,
                class: 'btn btn__primary landing-page__bonus__button',
                target: isInternalLink && '_blank',
            },
        }, text),
    ]);
}
function generateListTree(createElement, items, isOrdered) {
    if (isOrdered === void 0) { isOrdered = false; }
    var rootTag = isOrdered ? 'ol' : 'ul';
    var children = items.map(function (item) {
        if (Array.isArray(item)) {
            return generateListTree(createElement, item, isOrdered);
        }
        else {
            return createElement('li', createInnerHtml(item));
        }
    });
    return createElement(rootTag, {
        class: isOrdered && 'list-style-ordered',
    }, children);
}
function generateNestedListTree(createElement, items, isOrdered) {
    if (isOrdered === void 0) { isOrdered = false; }
    var rootTag = isOrdered ? 'ol' : 'ul';
    var children = items.map(function (item) {
        var result = [createElement('li', createInnerHtml(item.content))];
        if (item.items.length > 0) {
            result.push(createElement(rootTag, [generateNestedListTree(createElement, item.items, isOrdered)]));
        }
        return result;
    });
    var flatChildren = [];
    children.forEach(function (child) {
        child.forEach(function (nestedChild) {
            flatChildren.push(nestedChild);
        });
    });
    return createElement(rootTag, {
        class: isOrdered && 'list-style-ordered',
    }, flatChildren);
}
function generateImage(createElement, image) {
    var imageElement = createElement('img', {
        attrs: {
            src: image.url,
            alt: sanitizeStructuredWiki(image.caption),
        },
    });
    if (isImageWithLink(image)) {
        return createElement('a', {
            attrs: {
                href: image.caption,
            },
        }, [imageElement]);
    }
    else {
        return createElement('figure', [
            imageElement,
            createElement('figcaption', sanitizeStructuredWiki(image.caption)),
        ]);
    }
}
function generateHeader(createElement, header) {
    return createElement("h".concat(header.level), __assign(__assign({}, createInnerHtml(header.text)), { attrs: {
            id: header.anchor,
        } }));
}
function generateTable(createElement, table, Component) {
    return createElement(Component, {
        props: {
            headings: table.headings,
            body: table.body,
            sanitize: sanitizeStructuredWiki,
        },
    });
}

export { createInnerHtml, generateButton, generateHeader, generateImage, generateListTree, generateNestedListTree, generateTable };
